/*
  Subscription B2 controller.

  PRICES ARE A TABLE, NOT A CALCULATION. Each tier carries its own monthly / prepay /
  billed-today figure, edited in the tiers setting on the block.

  THE COMPARATOR IS COFFEE AT LIST, AND NOTHING ELSE. listValue() is bags x list price. The
  only percentage on the card is measured against it, so a shopper can check the claim with
  the multiplication in front of them. Shipping and the matchbook carry NO figure - they read
  "Included". The $75 membership kit is DISPLAYED but deliberately excluded from the
  percentage: the moment a discount leans on a number the shopper cannot verify, one bad line
  discredits the whole stack. Do not add shipping or the kit back into listValue().

  THE PAY FIGURE IS THE INTRO PRICE, box one, because that is what the shopper is agreeing to
  today. The ongoing price is disclosed immediately beneath it by upfront_line. Both numbers
  must stay visible together; showing only the intro is a bait, showing only the ongoing
  throws away the offer.

  THE COFFEE ROW LABEL IS BUILT HERE because its ounces move with the tier. The shape lives
  in coffee_line so the copy stays editable without touching this file. It carries no figure:
  the one struck comparator sits in the title row beside the price.

  amt() drops the cents when a figure is whole, so the membership kit reads "$75" rather than
  "$75.00", while a coffee total still reads "$98.97".

  PERCENTAGES NEED A NAMED COMPARATOR. The ones here point at the stack above them, or at a
  coffee shop drink. Do not add one that floats.

  FLOW: CTA -> popup 1 (quiz) -> popup 2 (prepay) -> STRAIGHT TO CHECKOUT.
  The redirect waits for a cart line carrying a SELLING PLAN, not merely for the cart to
  grow: the engine writes the bundle marker about a second before the coffee, and
  redirecting on "the cart grew" once shipped a membership-only cart with no coffee in it.

  These prices are MODELLED. Skio still bills its own numbers until the products and selling
  plans are repriced. The editor banner says so; it never renders on the storefront.
*/
(function () {
  /* LOADED FROM TWO PLACES ON PURPOSE: blocks/subscription-b2.liquid (so the buy box always has
     it) and layout/theme.liquid (so the cart guard below runs on EVERY page, including the
     individual coffee PDPs which use the old buy box and would otherwise let a $0 gift line sit
     in a cart with no subscription). Executing twice would double-bind every listener, so the
     first run claims the flag and the second returns immediately. */
  if (window.__heistB2Loaded) return;
  window.__heistB2Loaded = true;

  var POLL_MS = 120, MAX_TRIES = 50, wired = new WeakSet();

  function money(n) { return "$" + n.toFixed(2); }
  function whole(n) { return "$" + Math.round(n); }
  function amt(n) { return Math.round(n) === n ? whole(n) : money(n); }
  function put(el, text) { if (el && el.textContent !== text) el.textContent = text; }
  function fill(tpl, map) {
    var out = String(tpl || "");
    Object.keys(map).forEach(function (k) { out = out.split(k).join(map[k]); });
    return out;
  }

  function parseTiers(raw) {
    var out = [];
    String(raw || "").split(";").forEach(function (row) {
      var p = row.split("|");
      if (p.length < 6) return;
      var monthly = parseFloat(p[2]) || 0;
      out.push({
        oz: parseInt(p[0], 10) || 0,
        bags: parseInt(p[1], 10) || 0,
        monthly: monthly,
        prepay: parseFloat(p[3]) || 0,
        today: parseFloat(p[4]) || 0,
        cups: parseInt(p[5], 10) || 0,
        /* Box-one price. Absent means no step-up, so it falls back to the ongoing price. */
        intro: parseFloat(p[6]) || monthly
      });
    });
    return out;
  }

  /* tierIndex -> { standard, heist } selling plan ids. One row per tier, in tiers order. */
  function parsePlans(raw) {
    var out = [];
    String(raw || "").split(";").forEach(function (row) {
      var p = row.split("|");
      if (p.length < 2) return;
      out.push({ standard: String(p[0]).trim(), heist: String(p[1]).trim() });
    });
    return out;
  }

  function parseLadder(raw) {
    var out = [];
    String(raw || "").split(",").forEach(function (pair) {
      var p = pair.split(":");
      if (p.length !== 2) return;
      var q = parseInt(p[0], 10), pr = parseFloat(p[1]);
      if (q > 0 && isFinite(pr)) out.push({ qty: q, price: pr });
    });
    return out.sort(function (a, b) { return a.qty - b.qty; });
  }

  /* SELF-HEAL CARTS POISONED BEFORE THE FIX. Someone still carrying an undeliverable gift
     line from 2026-08-28/29 cannot check out at all, and nothing else would touch it. Strip
     them on every page load. Silent when there is nothing to remove. */
  /* Gift ids live at module scope because the sweep below runs outside init(). */
  var GIFT_ALWAYS = [56019741540674];
  var GIFT_PREPAID = [56019716636994, 56019716669762];
  var GIFT_DEAD = [55908122034498, 55908126720322, 55908126851394];
  var GIFT_CLEANUP = GIFT_ALWAYS.concat(GIFT_PREPAID, GIFT_DEAD);
  var healed = false;
  /* GIFTS MAY NEVER OUTLIVE THE COFFEE.

     They are $0 lines, so a cart holding only gifts is a free order. Deleting the coffee from
     the drawer used to leave the bear and the tin sitting there, checkoutable. The gifts are
     not a product anyone bought - they are a consequence of a subscription - so the rule is
     simply: no subscription line in the cart, no gifts in the cart.

     This also cleans up the undeliverable legacy variants (GIFT_DEAD), which additionally make
     the whole cart unshippable.

     Runs on page load AND after any cart mutation, because the drawer's own remove button is
     theme code we do not control - hooking fetch is the only way to hear about it. Skipped
     while data-b2-checkout is pending: mid-handoff the gifts are added moments after the
     subscription lines settle, and a sweep in that window would race them straight back out. */
  var sweeping = false;
  function sweepGifts() {
    if (sweeping) return Promise.resolve();
    if (document.documentElement.getAttribute("data-b2-checkout") === "pending") return Promise.resolve();
    sweeping = true;
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var items = c.items || [];
        var hasSub = items.some(function (i) { return !!i.selling_plan_allocation; });
        var orphans = items.filter(function (i) {
          if (GIFT_CLEANUP.indexOf(i.variant_id) === -1) return false;
          /* dead variants always go; live gifts go only when nothing is subscribed */
          return !hasSub || GIFT_DEAD.indexOf(i.variant_id) !== -1;
        });
        if (!orphans.length) return null;
        return orphans.reduce(function (chain, item) {
          return chain.then(function () {
            return fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: item.key, quantity: 0 })
            });
          });
        }, Promise.resolve()).then(function () { return true; });
      })
      .then(function (removed) {
        if (removed) document.dispatchEvent(new CustomEvent("b2:gifts-swept", { bubbles: true }));
        return removed;
      })
      .catch(function () {})
      .then(function (r) { sweeping = false; return r; });
  }

  /* Hear about cart changes made by the theme's own drawer. Pass-through wrapper - it never
     alters the request or the response, it only schedules a sweep after a cart write. */
  (function watchCartWrites() {
    if (!window.fetch || window.__b2CartWatch) return;
    window.__b2CartWatch = true;
    var orig = window.fetch;
    var timer = null;
    window.fetch = function (input, opts) {
      var url = "";
      try { url = typeof input === "string" ? input : (input && input.url) || ""; } catch (e) {}
      var isWrite = /\/cart\/(change|update|add|clear)(\.js)?/.test(url);
      return orig.apply(this, arguments).then(function (res) {
        if (isWrite) {
          clearTimeout(timer);
          timer = setTimeout(function () { sweepGifts(); }, 400);
        }
        return res;
      });
    };
  })();

  function init(root) {
    var ui = root.querySelector("[data-b2]");
    if (!ui || wired.has(root)) return;
    wired.add(root);

    /* sweepGifts runs from boot, sitewide - see the load guard above. */

    var TIERS = parseTiers(ui.dataset.tiers);
    var LIST = Number(ui.dataset.retailPerBag) || 0;
    var GIFT_VALUE = Number(ui.dataset.giftValue) || 0;
    var SHOP = Number(ui.dataset.shopPrice) || 0;
    var SHOP_TPL = ui.dataset.shopTemplate || "vs. [price] at a coffee shop";
    var HEADLINE_MODE = ui.dataset.headlineMode || "total";
    var PERBAG_SUFFIX = ui.dataset.perbagSuffix || "";
    var PERBAG_VS_TPL = ui.dataset.perbagVs || "";
    var SAVE_TPL = ui.dataset.saveTemplate || "";
    /* No fallback. An EMPTY flag_line must mean "no badge figure" - falling back to a
       hardcoded default put a percentage back on the card chrome that was removed on purpose. */
    var FLAG_TPL = ui.dataset.flagTemplate || "";
    var COFFEE_TPL = ui.dataset.coffeeTemplate || "[oz]oz Coffee ([cups] cups)";
    var UPFRONT_TPL = ui.dataset.upfrontTemplate || "[intro] today, then [monthly] every 4 weeks";
    var PREPAY_MONTHS = Number(ui.dataset.prepayMonths) || 3;
    var CUPS_PER_BAG = Number(ui.dataset.cupsPerBag) || 0;
    var LADDER = parseLadder(ui.dataset.ladder);
    var ONETIME_MIN = Math.max(1, Number(ui.dataset.onetimeMin) || 1);
    var FAST = ui.dataset.fastHandoff === "true";
    var PLANS = parsePlans(ui.dataset.plans);

    function q(sel) { return ui.querySelector(sel); }

    function tierIndex() {
      var b = ui.querySelector("[data-b2-tier][aria-pressed='true']");
      return b ? (parseInt(b.dataset.b2Tier, 10) || 0) : 0;
    }
    function tier() {
      return TIERS[tierIndex()] || TIERS[0] ||
        { oz: 0, bags: 0, monthly: 0, prepay: 0, today: 0, cups: 0, intro: 0 };
    }
    /* The one comparator: coffee at list. Checkable with a multiplication. Shipping and the
       membership kit are shown as included and are NOT in here - see the header note. */
    function listValue(t) {
      return t.bags * LIST;
    }

    /* Every settings-driven line carrying a live figure is a [data-b2-tpl] template, filled
       from one token map, so the card, the receipt and the prepay modal can never disagree. */
    function fillTokens(map) {
      ui.querySelectorAll("[data-b2-tpl]").forEach(function (el) {
        put(el, fill(el.getAttribute("data-b2-tpl"), map));
      });
    }

    /* ---------- solo mode (quiz_mode 'none') ----------

       The paid landing page sells ONE coffee, so there is nothing to ask. The compiled buy box
       still needs a box composition, and it only accepts one: a preset tile carrying
       "<variantId>:<qty>" on data-spec, clicked. The quiz used to write that; here the tier
       writes it directly.

       Applied on every tier change, not just at CTA time, for the same reason the quiz applied
       on every answer: finish() clicks the real submit 300ms after the tier button, and a spec
       written inside that window has raced before. By the time anyone presses the CTA the tile
       is already correct. */
    var SOLO = ui.dataset.quizMode === "none";

    function applySoloSpec() {
      if (!SOLO) return false;
      var vid = ui.dataset.soloVariant;
      var tile = root.querySelector("[data-b2-solo-tile]");
      var n = (tier() || {}).bags || 0;
      if (!vid || !tile || !n) return false;
      var spec = vid + ":" + n;
      if (tile.dataset.spec !== spec) tile.dataset.spec = spec;
      tile.click();
      return true;
    }

    /* No quiz and no prepay modal on this page: one decision, then checkout. */
    function soloCheckout() {
      if (!applySoloSpec()) return;
      finish(false);
    }

    function setTier(i) {
      ui.querySelectorAll("[data-b2-tier]").forEach(function (b) {
        b.setAttribute("aria-pressed", String((parseInt(b.dataset.b2Tier, 10) || 0) === i));
      });
      syncReceipt();
      applySoloSpec();
      ui.dispatchEvent(new CustomEvent("b2:tier", { bubbles: true }));
    }

    function setMode(mode) {
      ui.dataset.mode = mode;
      ui.querySelectorAll("[data-b2-tab]").forEach(function (t) {
        t.setAttribute("aria-pressed", String(t.dataset.b2Tab === mode));
      });
      syncOneTime();
    }

    function show(sel, open) { var el = q(sel); if (el) el.hidden = !open; }

    ui.addEventListener("click", function (e) {
      var tierBtn = e.target.closest("[data-b2-tier]");
      if (tierBtn) { setTier(parseInt(tierBtn.dataset.b2Tier, 10) || 0); return; }
      var tab = e.target.closest("[data-b2-tab]");
      if (tab) { setMode(tab.dataset.b2Tab); return; }
      if (e.target.closest("[data-b2-gifts-open]")) { show("[data-b2-gifts]", true); return; }
      if (e.target.closest("[data-b2-gifts-close]")) { show("[data-b2-gifts]", false); return; }
      if (e.target.closest("[data-b2-choose]")) { if (SOLO) { soloCheckout(); } else { openSelector(); } return; }
      if (e.target.closest("[data-b2-selector-back]")) { closeSelector(); return; }
      if (e.target.closest("[data-b2-selector-confirm]")) { toPrepay(); return; }
      if (e.target.closest("[data-b2-prepay-yes]")) { finish(true); return; }
      if (e.target.closest("[data-b2-prepay-no]")) { finish(false); return; }
      if (e.target.closest("[data-b2-prepay-back]")) { backToSelector(); return; }
      if (e.target.closest("[data-b2-onetime-add]")) { addOneTime(); return; }
      var step = e.target.closest("[data-b2-onetime-step]");
      if (step) { stepOneTime(step.dataset.b2OnetimeStep, step.dataset.variantId); return; }
    });

    function syncReceipt() {
      var t = tier();
      var coffee = listValue(t);
      var pay = t.intro || t.monthly;
      var saved = coffee - pay;
      var savedPct = coffee > 0 ? Math.round(saved / coffee * 100) : 0;

      put(q("[data-b2-cups]"), t.cups ? String(t.cups) : "");
      put(q("[data-b2-coffee-label]"), t.bags > 0 ? fill(COFFEE_TPL, {
        "[cups]": String(t.cups),
        "[oz]": String(t.oz),
        "[bags]": String(t.bags),
        "[list]": LIST.toFixed(2)
      }) : "");
      /* The coffee row carries no figure. The single struck comparator lives in the title
         row beside the price, which is the only place it can be checked against anything. */
      /* No figure on shipping. The label says "Included" and that is the whole claim. */
      put(q("[data-b2-gift-value]"), GIFT_VALUE > 0 ? amt(GIFT_VALUE) : "");
      /* HEADLINE: box total, or per bag with the box total moved into the CTA.
         In perbag mode the struck figure becomes the RETAIL PER BAG, so the pair beside the
         name compares like with like - what a bag costs here against what it costs at list.
         Striking the box total next to a per-bag figure would be comparing two different
         units, which is the one thing this card must never do. */
      if (HEADLINE_MODE === "perbag" && t.bags > 0 && pay > 0) {
        put(q("[data-b2-you-pay]"), money(pay / t.bags));
        put(q("[data-b2-total-value]"), LIST > 0 ? money(LIST) : "");
      } else {
        put(q("[data-b2-total-value]"), coffee > 0 ? money(coffee) : "");
        put(q("[data-b2-you-pay]"), pay > 0 ? whole(pay) : "");
      }

      var ctaWrap = q("[data-b2-cta-price-wrap]");
      if (ctaWrap) {
        if (pay > 0) {
          put(q("[data-b2-cta-price]"), whole(pay));
          put(q("[data-b2-cta-compare]"), coffee > 0 ? money(coffee) : "");
          ctaWrap.hidden = false;
        } else ctaWrap.hidden = true;
      }

      /* Same figure as the savings line, so the badge cannot contradict the receipt. */
      put(q("[data-b2-flagsave]"),
        saved > 0 && savedPct > 0 ? fill(FLAG_TPL, { "[pct]": String(savedPct) }) : "");

      /* WRITTEN AS TWO SPANS, NOT ONE STRING. On a phone the single line wrapped mid-phrase
         ("76% / LESS"), so the comparison gets its own element and CSS drops it to its own
         line below 749px. Desktop still reads as one line via the separator on .b2__cupvs. */
      var cupEl = q("[data-b2-percup]");
      var vsEl = q("[data-b2-percup-vs]");
      if (pay > 0 && t.cups > 0) {
        put(cupEl, money(pay / t.cups) + " a cup");
        var vs = "";
        if (SHOP > 0) {
          vs = SHOP_TPL.split("[price]").join(money(SHOP));
          var shopTotal = SHOP * t.cups;
          var shopPct = shopTotal > 0 ? Math.round((shopTotal - pay) / shopTotal * 100) : 0;
          if (shopPct > 0) vs += "  ·  " + shopPct + "% less";
        }
        put(vsEl, vs);
      } else {
        put(cupEl, "");
        put(vsEl, "");
      }

      var saveEl = q("[data-b2-save]");
      if (saveEl) {
        if (saved > 0 && SAVE_TPL) {
          saveEl.hidden = false;
          put(saveEl, fill(SAVE_TPL, { "[amt]": money(saved), "[pct]": String(savedPct) }));
        } else saveEl.hidden = true;
      }

      /* PER-BAG vs RETAIL. Solo paid page only - PERBAG_SUFFIX is blank everywhere else, so
         this whole block is inert on the main PDP. Divides the box-one price, which is the
         figure printed above it; the step-up line below states what changes at box two. */
      var bagEl = q("[data-b2-perbag]");
      var bagVsEl = q("[data-b2-perbag-vs]");
      if (bagEl || bagVsEl) {
        if (pay > 0 && t.bags > 0 && PERBAG_SUFFIX) {
          put(bagEl, money(pay / t.bags) + " " + PERBAG_SUFFIX);
          if (LIST > 0 && PERBAG_VS_TPL) {
            var vsParts = PERBAG_VS_TPL.split("[list]");
            put(q("[data-b2-perbag-pre]"), vsParts[0] || "");
            put(q("[data-b2-perbag-list]"), money(LIST));
            put(q("[data-b2-perbag-post]"), vsParts.length > 1 ? vsParts[1] : "");
            if (bagVsEl) bagVsEl.hidden = false;
          } else if (bagVsEl) bagVsEl.hidden = true;
        } else {
          put(bagEl, "");
          if (bagVsEl) bagVsEl.hidden = true;
        }
      }

      /* The step-up is disclosed here, one line under the price it steps up from. */
      put(q("[data-b2-upfront]"),
        pay > 0 ? fill(UPFRONT_TPL, {
          "[intro]": whole(pay),
          "[monthly]": whole(t.monthly)
        }) : "");

      syncPrepay();
      fillTokens({
        "[intro]": whole(pay),
        "[monthly]": whole(t.monthly),
        "[prepay]": whole(t.prepay),
        "[today]": whole(t.today),
        "[payg]": whole(pay + (t.monthly * (PREPAY_MONTHS - 1))),
        "[save]": whole((pay + (t.monthly * (PREPAY_MONTHS - 1))) - t.today),
        "[months]": String(PREPAY_MONTHS),
        "[bags]": String(t.bags),
        "[oz]": String(t.oz),
        "[cups]": String(t.cups),
        "[list]": money(coffee)
      });
    }

    /* THE MODAL COMPARES THREE-MONTH TOTALS, NOT MONTHLY RATES. Under the intro offer the
       prepay rate and the box-one rate are the SAME number, so two monthly figures side by
       side read as an identical offer twice. The difference only exists across the term:
       intro + two ongoing months versus the prepaid total. That gap is exactly $20 on every
       tier by construction, which is the claim the copy makes.

       NO PERCENTAGE IN HERE. The two things prepay buys are the $20 and the kit arriving with
       box one, and both are checkable against the figures shown. A percentage would need a
       comparator this screen does not display. */
    function syncPrepay() {
      var t = tier();
      var intro = t.intro || t.monthly;
      var payg = intro + (t.monthly * (PREPAY_MONTHS - 1));
      var extra = payg - t.today;

      put(q("[data-b2-m-monthly]"), intro > 0 ? whole(intro) : "");
      put(q("[data-b2-m-ongoing]"), t.monthly > 0 ? whole(t.monthly) : "");
      put(q("[data-b2-m-total]"), payg > 0 ? whole(payg) : "");
      put(q("[data-b2-p-monthly]"), t.prepay > 0 ? whole(t.prepay) : "");
      put(q("[data-b2-p-today]"), t.today > 0 ? whole(t.today) : "");
      put(q("[data-b2-p-extra]"), extra > 0 ? whole(extra) : "");
    }

    /* ---------- one-time tab ---------- */

    function unitPriceFor(qty) {
      if (!LADDER.length) return LIST;
      var price = LADDER[0].price;
      for (var i = 0; i < LADDER.length; i++) if (qty >= LADDER[i].qty) price = LADDER[i].price;
      return price;
    }
    function oneTimeQty() {
      var n = 0;
      ui.querySelectorAll("[data-b2-onetime-qty]").forEach(function (el) {
        n += parseInt(el.textContent, 10) || 0;
      });
      return n;
    }
    function stepOneTime(dir, variantId) {
      var el = ui.querySelector('[data-b2-onetime-qty][data-variant-id="' + variantId + '"]');
      if (!el) return;
      var n = parseInt(el.textContent, 10) || 0;
      n = dir === "up" ? n + 1 : Math.max(0, n - 1);
      el.textContent = String(n);
      syncOneTime();
    }
    function syncOneTime() {
      var qty = oneTimeQty(),
        unit = unitPriceFor(qty),
        subtotal = qty * unit,
        cups = Math.round(qty * CUPS_PER_BAG),
        freeBags = parseInt(ui.dataset.freeShipBags, 10) || 0,
        flat = Number(ui.dataset.flatShip) || 0,
        ship = qty < 1 ? 0 : (freeBags > 0 && qty >= freeBags ? 0 : flat),
        oz = Number(ui.dataset.ozPerBag) || 0;

      /* Highlight the HIGHEST rung reached, not every rung sharing a price. On a flat ladder
         every rung shares a price, so comparing prices lit all three at once. */
      var reached = -1;
      ui.querySelectorAll("[data-b2-rung]").forEach(function (r) {
        var at = parseInt(r.dataset.b2Rung, 10) || 0;
        if (qty >= at && at > reached) reached = at;
      });
      ui.querySelectorAll("[data-b2-rung]").forEach(function (r) {
        var at = parseInt(r.dataset.b2Rung, 10) || 0;
        r.setAttribute("aria-current", String(qty > 0 && at === reached));
      });
      put(q("[data-b2-onetime-cups]"), qty > 0 ? Math.round(qty * oz) + "oz · " + cups + " cups" : "");
      /* Divide by the UNROUNDED cup count. Rounding first made a flat per-bag price look
         like it moved between quantities. */
      var exactCups = qty * CUPS_PER_BAG;
      put(q("[data-b2-onetime-percup]"), qty > 0 && exactCups > 0 ? money(subtotal / exactCups) + " a cup" : "");
      put(q("[data-b2-onetime-subtotal]"), money(subtotal));
      put(q("[data-b2-onetime-ship]"), qty < 1 ? "" : (ship > 0 ? money(ship) : "Free"));
      /* TOTAL COST IS PRODUCT ONLY - SHIPPING IS DELIBERATELY EXCLUDED. Quoting a shipping
         figure here implies it is final, and it is not: the real rate is decided at checkout by
         address and cart value, so this line was promising a number the checkout could
         contradict. Showing the coffee total and letting checkout state shipping is the honest
         version. `ship` is still computed above because the free-shipping rung depends on it. */
      put(q("[data-b2-onetime-otd]"), qty > 0 ? money(subtotal) : money(0));
      /* THE MINIMUM HAS TO EXPLAIN ITSELF. A bare `disabled` below the threshold is the
         same dead button that read as broken on 2026-08-28: the shopper taps, nothing
         happens, they leave. So the label states what is missing at the moment it binds.
         Steppers still count real bags one at a time - the minimum is a CART total, so
         1 Blueprint + 1 Smooth Talker must remain a valid way to reach it. Never make a
         `+` jump two, and never let a displayed number mean anything but one bag, or the
         cart drawer will contradict the page. */
      var add = q("[data-b2-onetime-add]");
      if (add) {
        var shortBy = ONETIME_MIN - qty;
        add.disabled = shortBy > 0;
        var label;
        if (qty === 0) {
          label = add.dataset.labelEmpty || "Select your bags";
        } else if (shortBy > 0) {
          label = (add.dataset.labelShort || "Add [n] more — [min] bag minimum")
            .replace(/\[n\]/g, String(shortBy))
            .replace(/\[min\]/g, String(ONETIME_MIN));
        } else {
          label = add.dataset.labelReady || "Add to cart";
        }
        /* WRITE THE LABEL INTO ITS SPAN, NOT THE BUTTON. In perbag mode the button also holds
           the total, and writing button.textContent would delete it on the next quantity
           change. Falls back to the button itself for the standard layout, which has no span. */
        var labelEl = add.querySelector("[data-b2-onetime-label]") || add;
        if (labelEl.textContent.trim() !== label) labelEl.textContent = label;

        /* The total appears only once the button is actually usable. Showing "$32.99" beside
           "Add 1 more - 2 bag minimum" reads as the price of a thing you cannot buy. */
        var otWrap = q("[data-b2-onetime-price-wrap]");
        if (otWrap) otWrap.hidden = shortBy > 0 || qty < 1;
      }
    }
    /*
      THE ADD ALWAYS WORKED. Nothing ever SHOWED it, which read as a dead button.

      Two independent reasons, both silent. The opener lookup asked for
      [data-cart-drawer-open], `cart-drawer-opener button` and a[href="/cart"] - this theme has
      none of the three, all of them match zero elements, so a 200 from /cart/add.js put the bag
      in the cart and then did nothing at all. The real control is the header's own
      button.heist-cart, which owns the drawer through aria-controls="cart-drawer".

      And it dispatched cart:refresh, which nothing in this theme listens for. Same dead end the
      subscription path hit. Ask /cart/add.js for the cart sections in the SAME round trip and
      swap the drawer markup directly, or the drawer opens showing the pre-add cart.
    */
    function openCartDrawer() {
      var opener =
        document.querySelector('[aria-controls="cart-drawer"]') ||
        document.querySelector(".heist-cart") ||
        document.querySelector("[data-cart-drawer-open], cart-drawer-opener button");
      if (opener && opener.click) opener.click();
    }

    function addOneTime() {
      var items = [];
      ui.querySelectorAll("[data-b2-onetime-qty]").forEach(function (el) {
        var n = parseInt(el.textContent, 10) || 0;
        if (n > 0) items.push({ id: Number(el.dataset.variantId), quantity: n });
      });
      if (!items.length) return;
      var btn = q("[data-b2-onetime-add]");
      if (btn) btn.disabled = true;

      var body = { items: items };
      var id = cartSectionId();
      if (id) {
        body.sections = id;
        body.sections_url = window.location.pathname;
      }

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      })
        .then(function (r) {
          return r.json().then(function (j) { return { ok: r.ok, data: j }; });
        })
        .then(function (res) {
          /* A 422 here is a real outcome, not an exception - leave the cart alone and let the
             button come back rather than opening a drawer that shows nothing new. */
          if (!res.ok) return null;
          if (id && res.data && res.data.sections) return swapCartSection(res.data, id);
          return refreshCartUI();
        })
        .then(function () { openCartDrawer(); })
        .catch(function () {})
        .then(function () { if (btn) btn.disabled = false; });
    }

    /* ---------- popups ---------- */

    var lastFocus = null;
    function lockScroll(on) { document.documentElement.style.overflow = on ? "hidden" : ""; }
    function openSelector() {
      var sel = q("[data-b2-selector]");
      if (!sel) return;
      lastFocus = document.activeElement;
      sel.hidden = false;
      lockScroll(true);
      var first = sel.querySelector("[data-quiz]");
      if (first && first.focus) first.focus();
      document.addEventListener("keydown", onKey);
    }
    function closeSelector() {
      show("[data-b2-selector]", false);
      lockScroll(false);
      document.removeEventListener("keydown", onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function toPrepay() { show("[data-b2-selector]", false); syncPrepay(); show("[data-b2-prepay]", true); }
    function backToSelector() { show("[data-b2-prepay]", false); show("[data-b2-selector]", true); }
    function closeAll() {
      show("[data-b2-selector]", false);
      show("[data-b2-prepay]", false);
      lockScroll(false);
      document.removeEventListener("keydown", onKey);
    }
    function onKey(e) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      var gifts = q("[data-b2-gifts]");
      if (gifts && !gifts.hidden) { show("[data-b2-gifts]", false); return; }
      var prepay = q("[data-b2-prepay]");
      if (prepay && !prepay.hidden) { backToSelector(); return; }
      closeSelector();
    }

    /* ---------- checkout ---------- */

    function cartState() {
      return fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          var keys = {};
          (c.items || []).forEach(function (i) { keys[String(i.key)] = true; });
          return {
            n: c.item_count || 0,
            keys: keys,
            raw: c,
            sub: (c.items || []).some(function (i) { return !!i.selling_plan_allocation; })
          };
        })
        .catch(function () { return { n: 0, sub: false }; });
    }
    function goCheckout() { window.location.href = "/checkout"; }

    /* THE THEME OPENS THE CART DRAWER WHENEVER SOMETHING IS ADDED. That is correct everywhere
       else and wrong here: this flow never shows a cart, it goes straight to checkout, so the
       drawer slid out mid-handoff and rendered as an empty white panel because the cart was
       still being written. The auto-open lives as an attribute on <cart-drawer-component>, so
       it is removed for the duration and put back afterwards, and CSS hides #cart-drawer while
       data-b2-checkout is pending in case something opens it another way. */
    function suppressDrawer(on) {
      var comp = document.querySelector("cart-drawer-component");
      if (comp) {
        if (on && comp.hasAttribute("auto-open")) {
          comp.setAttribute("data-b2-autoopen", "");
          comp.removeAttribute("auto-open");
        } else if (!on && comp.hasAttribute("data-b2-autoopen")) {
          comp.setAttribute("auto-open", "");
          comp.removeAttribute("data-b2-autoopen");
        }
      }
      if (on) closeDrawer();
    }

    function closeDrawer() {
      var dlg = document.querySelector("#cart-drawer dialog");
      if (dlg && dlg.open) { try { dlg.close(); } catch (e) {} }
    }

    /* THE $0 HEIST SOCIETY GIFTS.

       WHICH VARIANTS: NOT the original gift products. Those (55908122034498 / 55908126720322)
       are undeliverable - Shopify returns NO shipping rate for them even alone in a cart, with
       correct weight, stock, status, location and delivery profile. A cart holding one cannot
       be paid for at all: "These products are not available for delivery to United States".
       That broke real prepaid checkouts for ~18 hours on 2026-08-28/29. They are in GIFT_DEAD,
       stripped on sight and never added.

       The replacements were made with productCreate + productVariantsBulkUpdate +
       publishablePublish, and each was verified to return a live shipping rate ALONE in a cart
       before being wired in. If you add another gift, run that check first: identical-looking
       product settings are not evidence - the broken ones looked identical.

       WHO GETS WHAT:
         matchbook  -> every subscription, monthly and prepaid alike
         bear + tin -> prepaid only. That is the prepay pitch: the kit arrives with box one.

       Runs AFTER the plan repair, never before. Failure here must never block the handoff. */
    /* GIFT_ALWAYS / GIFT_PREPAID / GIFT_DEAD are module scope - see sweepGifts. */
    var GIFTS_ENABLED = true;

    function syncSocietyGifts(wantPrepaid, items) {
      var want = [];
      if (GIFTS_ENABLED) {
        want = GIFT_ALWAYS.slice();
        if (wantPrepaid) want = want.concat(GIFT_PREPAID);
      }

      var present = {};
      (items || []).forEach(function (i) { present[i.variant_id] = i.key; });

      var drop = [];
      GIFT_DEAD.concat(GIFT_ALWAYS, GIFT_PREPAID).forEach(function (id) {
        if (present[id] && want.indexOf(id) === -1) drop.push(present[id]);
      });

      var add = want
        .filter(function (id) { return !present[id]; })
        .map(function (id) { return { id: id, quantity: 1 }; });

      var chain = drop.reduce(function (c, key) {
        return c.then(function () {
          return fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
        });
      }, Promise.resolve());

      if (!add.length) return chain.catch(function () {});
      return chain
        .then(function () {
          return fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: add })
          });
        })
        .catch(function () {});
    }

    /* THE ENGINE PICKS A PLAN BY MATCHING A FRAGMENT OF ITS NAME, AND THAT MATCH IS NOW
       AMBIGUOUS. "week" matches all four monthly plans and "prepaid" all four prepaid ones, so
       it takes whichever comes first - the LEGACY plan. Measured 2026-08-28: a 3-bag cart built
       $56.15 on the old 18% plan instead of $69.00. So the cart is corrected here, after the
       engine assembles it, before checkout. Four rules, each one paid for:

       ONLY MODIFY LINES ALREADY IN THE CART. Re-adding the $0 Heist Society bundle is refused
       ("This product is part of a limited promotion") because it is gated to the engine's own
       add path. An early version cleared and re-added everything and left an empty checkout.

       ONLY TOUCH LINES THIS FLOW CREATED. priorKeys is the cart before the engine wrote. A
       shopper who adds a box, changes their mind and starts again must not have the first
       selection re-priced - that shipped once as 4 bags on the "(3 bags)" plan at $92.

       ADDRESS LINES BY KEY, NOT INDEX. Line keys are stable; indexes renumber after every write.
       Keying lets the whole repair run from ONE snapshot instead of re-reading between writes,
       which is what made the handoff to checkout slow.

       DEDUPE, NEVER SUM. The engine sometimes writes twice; a 4-bag box arrived as 8 bags over
       two lines. Keep the largest line per coffee and zero the rest. */
    function cartJSON() {
      return fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) { return r.json(); });
    }

    /* ASK FOR THE RE-RENDERED CART SECTION AND SWAP IT IN. /cart/change.js updates the cart
       but leaves the drawer showing whatever HTML was rendered when the ENGINE added, i.e. the
       legacy prices. Reported twice: a 2-bag monthly drawer reading $54.10 (2 x 18%-off legacy)
       against $49.00 at checkout, and a 2-bag prepay reading $130.59 against $147.00.

       Dispatching a cart:refresh event did NOT fix it - this theme does not rebuild from that.
       It rebuilds from `sections` returned on the cart request, which is exactly what the
       theme's own cart script does, so this mirrors it. */
    function swapCartSection(res, id) {
      if (!res || !res.sections || !id || !res.sections[id]) return;
      try {
        var doc = new DOMParser().parseFromString(res.sections[id], "text/html");
        var targets = ["cart-items-component", ".cart-drawer__inner"];
        for (var i = 0; i < targets.length; i++) {
          var next = doc.querySelector(targets[i]);
          var cur = document.querySelector(targets[i]);
          if (next && cur) { cur.replaceWith(next); return; }
        }
      } catch (e) {}
    }

    function cartSectionId() {
      var el = document.querySelector("cart-items-component");
      return el && el.dataset ? el.dataset.sectionId : null;
    }

    function changeLine(body) {
      var id = cartSectionId();
      if (id) {
        body.sections = id;
        body.sections_url = window.location.pathname;
      }
      return fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok) return false;
        return r.json().then(function (j) { swapCartSection(j, id); return true; })
          .catch(function () { return true; });
      }).catch(function () { return false; });
    }

    /* Plans the whole repair from one snapshot and returns the ops to run. */
    function planOps(target, priorKeys, items) {
      var mine = (items || []).filter(function (it) {
        return it.selling_plan_allocation && !priorKeys[String(it.key)];
      });
      var best = {};
      mine.forEach(function (it) {
        var v = String(it.variant_id);
        if (!best[v] || it.quantity > best[v].quantity) best[v] = it;
      });
      var ops = [];

      /* The engine may add its own copy of the $0 membership bundle on top of ours; Shopify
         merges them into one line of two. Clamp it back to one. */
      var memVid = Number(ui.dataset.membershipVariant || 0);
      if (memVid) {
        (items || []).forEach(function (it) {
          if (Number(it.variant_id) === memVid && it.quantity > 1) {
            ops.push({ id: it.key, quantity: 1 });
          }
        });
      }

      mine.forEach(function (it) {
        if (best[String(it.variant_id)] !== it) {
          ops.push({ id: it.key, quantity: 0 });
          return;
        }
        if (String(it.selling_plan_allocation.selling_plan.id) !== String(target)) {
          ops.push({ id: it.key, quantity: it.quantity, selling_plan: Number(target) });
        }
      });
      return ops;
    }

    function runOps(ops) {
      return ops.reduce(function (p, op) {
        return p.then(function () { return changeLine(op); });
      }, Promise.resolve());
    }

    /* WAIT UNTIL THE CART HOLDS EVERY BAG THE TIER PROMISED, THEN REPAIR.

       A MIXED BOX IS WRITTEN AS SEVERAL LINES, ONE PER COFFEE, AND NOT ALL AT ONCE. Repairing
       from the first snapshot that showed any subscription line fixed only the lines that
       existed at that instant; coffees the engine wrote afterwards kept the legacy plan.
       Measured: a 4-bag prepay box of 1 Blueprint + 1 quarter-caf + 2 decaf came out at
       $254.04, with two lines correct at $66.75 and the decaf pair still on the old plan at
       $60.27. An all-one-coffee box hides this completely, which is why it passed before.

       So we wait on the BAG COUNT, not on elapsed time and not on "a subscription line exists".
       We know exactly how many bags the pressed tier owes, so this resolves the moment the cart
       is complete rather than after a fixed delay - it is both safer and faster than the
       stability loop it replaces. The timeout is a backstop, not the normal path. */
    function waitForBags(expected, deadline) {
      return cartJSON().then(function (c) {
        var bags = (c.items || []).reduce(function (n, i) {
          return n + (i.selling_plan_allocation ? i.quantity : 0);
        }, 0);
        if (bags >= expected || Date.now() > deadline) return c;
        return new Promise(function (res) { setTimeout(res, 120); })
          .then(function () { return waitForBags(expected, deadline); });
      });
    }

    /* REPAIR IN A LOOP, RE-READING BEFORE EVERY PASS, UNTIL THERE IS NOTHING LEFT TO FIX.

       A fixed two-pass version left a line behind on a mixed box: a 4-bag prepay of 1 Blueprint
       + 1 quarter-caf + 2 decaf came out at $254.04 with the decaf pair still on the legacy
       plan. Changing a line's selling plan makes Shopify drop and re-add it, which reorders the
       cart and invalidates the keys captured in the same snapshot, so later ops in a batch can
       silently no-op. Re-reading before each pass means every op is built from keys that are
       currently valid.

       It stops the moment a pass finds nothing wrong, so the common case is one read and one
       write. The pass cap is a guard against a cart that cannot be satisfied, not a budget. */
    function repairLoop(target, priorKeys, pass) {
      pass = pass || 0;
      if (pass > 5) return Promise.resolve();
      return cartJSON().then(function (c) {
        var ops = planOps(target, priorKeys, c.items);
        if (!ops.length) return;
        return runOps(ops).then(function () {
          return repairLoop(target, priorKeys, pass + 1);
        });
      });
    }

    /* RE-FETCH THE CART SECTION AND SWAP IT IN. This has to be a real request, not an event.

       Dispatching cart:refresh was the first attempt and it does nothing - this theme rebuilds
       the drawer from `sections` HTML returned by the Section Rendering API, not from an event.

       It matters most on BACK FROM CHECKOUT: the page is restored from bfcache exactly as it
       was left, so the drawer still holds the markup rendered when the ENGINE added, with the
       legacy prices. Reported twice against a correct cart - a 2-bag monthly drawer reading
       $54.10 (2 x 18%-off legacy) against $49.00, and a 2-bag prepay reading $130.59
       (Blueprint $70.32 + Smooth Talker $60.27) against $147.00. */
    function refreshCartUI() {
      var id = cartSectionId();
      if (!id) return Promise.resolve();
      /* Use the CART ajax api, not the standalone Section Rendering API. `?sections=<id>` on a
         product URL does NOT serve the cart drawer - it answered with {"product": ...} and the
         swap silently did nothing. /cart/update.js with an empty `updates` is a no-op that
         returns the re-rendered cart sections, and is exactly how the theme's own cart script
         refreshes the drawer. */
      return fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          updates: {},
          sections: id,
          sections_url: window.location.pathname
        })
      })
        .then(function (r) { return r.json(); })
        .then(function (j) { swapCartSection(j, id); })
        .catch(function () {});
    }

    /* `skipRefresh` exists because refreshCartUI costs a full /cart/update.js round trip -
       measured at 1.2s on mobile - and its only job is to re-render a cart drawer the shopper
       is about to navigate away from. On the checkout path that is pure dead time. Everywhere
       else the refresh still matters, so it stays on by default.

       repairLoop already re-reads the cart on its final pass, so it hands that snapshot back
       rather than making the caller fetch /cart.js again. That is another ~200ms saved and,
       more importantly, one less chance for the two to disagree. */
    function applyPlan(target, priorKeys, expectedBags, skipRefresh) {
      if (!target) return Promise.resolve(null);
      priorKeys = priorKeys || {};
      return waitForBags(expectedBags || 0, Date.now() + 4000)
        .then(function () { return repairLoop(target, priorKeys, 0); })
        .then(function () {
          if (skipRefresh) return cartJSON();
          return refreshCartUI().then(cartJSON);
        })
        .catch(function () { return null; });
    }

    function abortHandoff() {
      document.documentElement.removeAttribute("data-b2-checkout");
      suppressDrawer(false);
      lockScroll(false);
      var pre = q("[data-b2-prepay]");
      if (pre) pre.hidden = false;
      lockScroll(true);
    }

    function planFor(wantPrepaid) {
      var m = PLANS[tierIndex()];
      if (!m) return null;
      return wantPrepaid ? m.heist : m.standard;
    }
    /* ---------- FAST HANDOFF ----------

       THE OLD PATH BUILDS THE CART WRONG ON PURPOSE AND THEN FIXES IT. The compiled buy box
       adds lines on whichever plan its own name-matching picks - historically the LEGACY plan -
       so we polled until the cart settled and issued a /cart/change.js per line to re-plan it.
       Measured 7.1s, and every one of those moving parts has broken production at least once.

       /cart/add.js takes `selling_plan` PER LINE. So the whole box, coffees and $0 gifts
       together, goes in one call already correct. Measured on live: 2.87s for a mixed 3-bag
       monthly box, 1.41s for a 3-bag prepaid box with all three gifts, and the prepaid cart
       returned live shipping rates - the exact thing that was unpayable for 18 hours on 08-29.

       Cart permalinks were tried first and REJECTED: /cart/<variant>:<qty>?selling_plan=<id>
       adds the bags but silently drops the plan, turning a $49 subscription into a $65.98
       one-time purchase. Do not revisit that route.

       NO /cart/clear.js HERE, EVER. Clearing and re-adding is what once left an empty checkout,
       because the gated $0 bundle refuses to be re-added. Instead we zero only the line keys
       THIS flow created last time, tracked in sessionStorage so a reload cannot orphan them.
       Lines the shopper added themselves are never touched.

       Falls back to the legacy path by returning false if the box cannot be resolved, so a
       broken spec degrades to "slow" rather than "no coffee in the cart". */
    /* IDENTIFY OUR PREVIOUS LINES BY PLAN, NOT BY KEY. The first version stored the line keys
       returned by /cart/add.js and zeroed them on the next run. It did not work: a line
       carrying a selling plan is RE-KEYED after the add, so the key the add hands back is not
       the key the line settles on. Measured on live - stored 54943232557378:1bcd82..., the
       cart came to rest holding 54943232557378:b15452..., nothing matched, and re-selecting a
       different tier left BOTH boxes in the cart at $118.

       Plan id is stable and unforgeable: the only way a cart line can carry one of these tier
       plans is through this buy box. So sweep every line on one of our plans plus our gift
       variants, then add fresh. Stateless, so it also survives a reload or a back button,
       which the sessionStorage version did not. */
    /* Both arms write "<variantId>:<qty>,..." onto a [data-preset-tile] - the quiz in the
       normal flow, applySoloSpec on the landing page. One selector covers both. */
    function boxSpec() {
      var tile = root.querySelector("[data-preset-tile]");
      var raw = tile && tile.dataset ? tile.dataset.spec || "" : "";
      return raw.split(",").map(function (chunk) {
        var bits = String(chunk).split(":");
        var id = parseInt(bits[0], 10), qty = parseInt(bits[1], 10);
        return id && qty > 0 ? { id: id, quantity: qty } : null;
      }).filter(Boolean);
    }

    function ourPlanIds() {
      var ids = {};
      (PLANS || []).forEach(function (m) {
        if (!m) return;
        if (m.standard) ids[String(m.standard)] = true;
        if (m.heist) ids[String(m.heist)] = true;
      });
      return ids;
    }

    function zeroLine(key) {
      return fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity: 0 })
      }).then(function (r) { return r.ok; }).catch(function () { return false; });
    }

    function dropPriorFastLines() {
      var mine = ourPlanIds();
      return cartJSON().then(function (c) {
        var doomed = (c.items || []).filter(function (i) {
          var planId = i.selling_plan_allocation && i.selling_plan_allocation.selling_plan
            ? String(i.selling_plan_allocation.selling_plan.id) : null;
          if (planId && mine[planId]) return true;
          return GIFT_CLEANUP.indexOf(i.variant_id) !== -1;
        });
        if (!doomed.length) return null;
        return doomed.reduce(function (chain, i) {
          return chain.then(function () { return zeroLine(i.key); });
        }, Promise.resolve());
      }).catch(function () {});
    }

    function finishFast(wantPrepaid) {
      var target = planFor(wantPrepaid);
      var box = boxSpec();
      if (!target || !box.length) return false;

      /* The tier is the contract. If the spec does not add up to the bags the shopper pressed,
         something upstream is out of sync - hand back to the legacy path rather than sell a
         box nobody chose. */
      var wantBags = (tier() || {}).bags || 0;
      var gotBags = box.reduce(function (n, i) { return n + i.quantity; }, 0);
      if (wantBags && gotBags !== wantBags) return false;

      var items = box.map(function (i) {
        return { id: i.id, quantity: i.quantity, selling_plan: Number(target) };
      });
      if (GIFTS_ENABLED) {
        var gifts = GIFT_ALWAYS.slice();
        if (wantPrepaid) gifts = gifts.concat(GIFT_PREPAID);
        gifts.forEach(function (id) { items.push({ id: id, quantity: 1 }); });
      }

      closeAll();
      document.documentElement.setAttribute("data-b2-checkout", "pending");
      suppressDrawer(true);

      dropPriorFastLines()
        .then(function () {
          return fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: items })
          });
        })
        .then(function (r) {
          if (!r.ok) throw new Error("add " + r.status);
          return r.json();
        })
        .then(function (res) {
          var added = (res && res.items) || [];
          if (!added.length) throw new Error("nothing added");
          closeDrawer();
          goCheckout();
        })
        .catch(function () { abortHandoff(); });

      return true;
    }

    function finish(wantPrepaid) {
      if (FAST && finishFast(wantPrepaid)) return;
      var want = wantPrepaid ? "heist" : "standard";
      var tierBtn = root.querySelector('[data-select-tier="' + want + '"]');
      if (tierBtn) tierBtn.click();
      var submit = root.querySelector("[data-buy-box-submit]");
      closeAll();
      document.documentElement.setAttribute("data-b2-checkout", "pending");
      var target = planFor(wantPrepaid);
      suppressDrawer(true);
      /* NEVER SEND A SHOPPER TO CHECKOUT WITH NO COFFEE IN THE CART. An empty /checkout
         bounces to /cart, and this theme redirects /cart to the HOMEPAGE, so the symptom is a
         long pause followed by the storefront home - which is what a shopper reported. If no
         subscription line exists by the time we are ready to hand off, abort the handoff,
         unwind the flow and put the shopper back on the PDP with the panel open so they can
         retry, rather than dumping them somewhere they did not ask to go. */
      var done = function (before) {
        /* skipRefresh: heading straight to checkout, so do not re-render the drawer. */
        applyPlan(target, before.keys, (tier() || {}).bags, true)
          .then(function (c) { return c || cartJSON(); })
          .then(function (c) {
            var hasSub = (c.items || []).some(function (i) { return !!i.selling_plan_allocation; });
            if (!hasSub) { abortHandoff(); return; }
            return syncSocietyGifts(wantPrepaid, c.items).then(function () {
              closeDrawer();
              goCheckout();
            });
          })
          .catch(abortHandoff);
      };
      cartState().then(function (before) {
        /* DO NOT SHORTEN THIS PRE-SUBMIT DELAY. The engine needs a beat after the tier click
           before the submit will take; cutting it to 120ms made the add silently no-op and the
           cart came out empty. The handoff was slow because of a cart stability LOOP that used
           to run afterwards, not because of this - that loop is gone and the repair now runs
           from a single snapshot, which is where the seconds actually were.
           Poll cap is ~6s, after which we hand off rather than stall. */
        setTimeout(function () { if (submit) submit.click(); }, 300);
        var tries = 0;
        (function poll() {
          if (++tries > 50) { done(before); return; }
          cartState().then(function (now) {
            if (now.n > before.n && now.sub) { done(before); return; }
            setTimeout(poll, 120);
          });
        })();
      });
    }

    /* BACK FROM CHECKOUT RESTORES THIS PAGE FROM bfcache EXACTLY AS IT WAS LEFT - mid-flow,
       with the quiz or prepay panel still open and data-b2-checkout still pending, which also
       kept the drawer suppressed and the page scroll-locked. Reset on every pageshow so back
       always lands on a clean PDP. */
    function resetFlow() {
      document.documentElement.removeAttribute("data-b2-checkout");
      lockScroll(false);
      document.body.style.overflow = "";
      ["[data-b2-selector]", "[data-b2-prepay]", "[data-b2-gifts]"].forEach(function (sel) {
        var el = q(sel);
        if (el) el.hidden = true;
      });
      suppressDrawer(false);

      /* THE DIM PAGE AFTER PRESSING BACK IS AN OPEN <dialog>, NOT ONE OF OUR PANELS. Hiding the
         quiz and prepay panels was not enough: the cart drawer's dialog comes back from bfcache
         still open, and its ::backdrop greys the whole page until something is clicked. Close
         every open dialog and clear the drawer's own open state, not just ours. */
      document.querySelectorAll("dialog[open]").forEach(function (d) {
        try { d.close(); } catch (e) {}
      });
      var td = document.getElementById("cart-drawer");
      if (td) {
        td.removeAttribute("open");
        td.classList.remove("is-open", "open", "active");
      }

      /* A page restored from bfcache carries the drawer markup as it was when the shopper left
         - which on the way to checkout is the pre-repair, legacy-priced version. Rebuild it. */
      refreshCartUI();
    }
    window.addEventListener("pageshow", resetFlow);
    resetFlow();

    setMode(ui.dataset.mode || "join");
    syncReceipt();
    syncOneTime();

    /* PRIME THE TILE FOR THE DEFAULT TIER, AND KEEP TRYING UNTIL IT TAKES.

       A single call here is too early. We boot on DOMContentLoaded; the compiled buy-box bundle
       initialises later, and a tile click before that is silently a no-op. The visible symptom
       was the carousel: the solo page loaded showing the shell product's own media and only
       swapped to the Smooth Talker gallery once the shopper touched the tier selector. The box
       composition was equally unset until then, which the CTA would have papered over but the
       first-impression images would not.

       So retry on a short interval and stop as soon as the gallery actually changes, which is
       the observable proof the bundle took the click. Bounded at ~3s either way. applySoloSpec
       is idempotent - same spec, same tile - so extra passes cost nothing. */
    if (SOLO) {
      var firstSlide = function () {
        var im = document.querySelector(".product-media img, slideshow-slide img");
        return im ? (im.currentSrc || im.src || "") : "";
      };
      var wasSlide = firstSlide();
      var soloTries = 0;
      var soloTimer = setInterval(function () {
        applySoloSpec();
        var now = firstSlide();
        if (++soloTries >= 12 || (now && now !== wasSlide)) clearInterval(soloTimer);
      }, 250);
      applySoloSpec();

      /* START THE ONE-TIME PANE AT THE MINIMUM. With one coffee on the page there is nothing to
         choose, so opening at zero just puts a disabled button in front of the shopper and asks
         them to press + twice to reach a quantity we already require. The main PDP still opens
         at zero on purpose - there the zero state is the shopper picking WHICH coffees. */
      var otRow = ui.querySelector("[data-b2-onetime-qty]");
      if (otRow && (parseInt(otRow.textContent, 10) || 0) === 0) {
        otRow.textContent = String(ONETIME_MIN);
        syncOneTime();
      }
    }
  }

  function boot() {
    document.querySelectorAll('buy-box[data-variation="presets"]').forEach(init);
  }
  function watchForBlock() {
    var tries = 0, t = setInterval(function () {
      if (++tries >= MAX_TRIES) clearInterval(t);
      boot();
    }, POLL_MS);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(); watchForBlock(); sweepGifts(); });
  } else { boot(); watchForBlock(); sweepGifts(); }
})();
